import React, { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sparkles, RefreshCw } from 'lucide-react';

/**
 * AI Summary Panel Component
 * 
 * Usage:
 * <AISummaryPanel 
 *   summaryType="PARTICIPANT" 
 *   entityId={participantId}
 *   title="AI Summary"
 * />
 */
export function AISummaryPanel({ summaryType, entityId, entityType, title = 'AI Summary' }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/summary/generate', {
        summaryType,
        entityId,
        entityType
      });
      setSummary(res.data.data);
      setGenerated(true);
      toast.success('Summary generated');
    } catch (err) {
      toast.error('Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="ai-summary-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleGenerate}
            disabled={loading}
            data-testid="generate-summary-btn"
          >
            {loading ? (
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3 mr-1" />
            )}
            {generated ? 'Regenerate' : 'Generate'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!generated && !loading && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Click "Generate" to create an AI-powered summary
          </p>
        )}
        {loading && (
          <div className="flex items-center justify-center py-6">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {generated && summary && (
          <div className="space-y-3">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {summary.summaryText || summary.summary || 'No summary available'}
            </p>
            {summary.generatedAt && (
              <p className="text-[10px] text-muted-foreground">
                Generated: {new Date(summary.generatedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AISummaryPanel;
